/* eslint-disable react-hooks/exhaustive-deps */
import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useQuery } from 'react-query';

import { useFormik } from 'formik';

import getMyschedule from 'api/schedule/getMyschedule';
import getUserDetail from 'api/users/getUserDetail';

import { ISelectOptionType } from 'components/atom/Form/Select/types';

import { TypeInvitation } from '../pages/Schedule/components/Invitations/types';
import schema from './schema';
import { IUseControllerContextProps, pageType } from './types';

const useControllerContext = createContext<IUseControllerContextProps>(
  {} as IUseControllerContextProps,
);

interface IUseControllerProviderProps {
  children: ReactNode;
}

const UseControllerProvider: React.FC<IUseControllerProviderProps> = ({ children }) => {
  const [pageActive, setPageActive] = useState<pageType>('schedule');
  const [prospectionId, setProspectionId] = useState<string | null>(null);
  const [scheduleData, setScheduleData] = useState<IGroupedTimeSlots | null>(null);
  const [hoursAvailable, setHoursAvailable] = useState<ISelectOptionType[]>([]);
  const [tab, setTab] = useState<number>(0);
  const [typeInvitation, setTypeInvitation] = useState<TypeInvitation>('invite');
  const [invitationData, setInvitationData] = useState<IMatchmakingInvitation | null>(null);
  const [invitationDataSuggested, setInvitationDataSuggested] =
    useState<IMatchmakingInvitation | null>(null);
  const [prospectionListData, setProspectionListData] = useState<IProspection | null>(null);

  // * - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

  const {
    data: prospectionData = null,
    isLoading: isProspectionLoading,
    refetch,
  } = useQuery(['getUserDetail', prospectionId], () => getUserDetail(prospectionId || ''), {
    enabled: !!prospectionId,
  });

  useEffect(() => {
    if (prospectionId) {
      refetch();
    }
  }, [prospectionId]);

  // * - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

  const { isLoading: isScheduleLoading } = useQuery(
    ['getMyschedule', prospectionData || invitationDataSuggested],
    () => getMyschedule(String(prospectionData?.user.id)),
    {
      enabled: !!prospectionData || !!invitationDataSuggested,
      onSuccess(data) {
        setScheduleData(data);
      },
    },
  );

  // * - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

  const handleProceed = useCallback(() => {
    setPageActive('preview');
  }, []);

  const formik = useFormik({
    initialValues: { date: '', hour: '', message: '' },
    onSubmit: handleProceed,
    validationSchema: schema,
  });

  const { values } = formik;

  // * - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

  useEffect(() => {
    if (scheduleData && values.date) {
      const data = scheduleData[values.date].map((hour) => ({
        value: hour,
        label: hour.substring(0, 5),
      }));

      setHoursAvailable(data || []);
    }
  }, [scheduleData, values.date]);

  // * - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

  const contextValue = useMemo(
    () => ({
      isLoading: isProspectionLoading || isScheduleLoading,

      prospectionId,
      setProspectionId,

      prospectionData,

      pageActive,
      setPageActive,

      scheduleData,

      formik,

      hoursAvailable,

      tab,
      setTab,

      typeInvitation,
      setTypeInvitation,

      invitationData,
      setInvitationData,

      invitationDataSuggested,
      setInvitationDataSuggested,

      prospectionListData,
      setProspectionListData,
    }),
    [
      isProspectionLoading,
      prospectionData,
      prospectionId,
      setProspectionId,
      pageActive,
      setPageActive,
      scheduleData,
      formik,
      hoursAvailable,
      tab,
      setTab,
      typeInvitation,
      setTypeInvitation,
      invitationData,
      setInvitationData,
      invitationDataSuggested,
      setInvitationDataSuggested,
      prospectionListData,
      setProspectionListData,
    ],
  );

  return (
    <useControllerContext.Provider value={contextValue}>{children}</useControllerContext.Provider>
  );
};

const useController = (): IUseControllerContextProps => {
  const context = useContext(useControllerContext);
  if (!context) throw new Error('useController must be used within an UseControllerProvider');
  return context;
};

export { UseControllerProvider, useController };
